import React, { createContext, useContext, useState, useCallback } from 'react';
import type { User } from '../types/user';
import { clearUserSession } from '../utils/session';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (user: User) => void;
  updateUser: (partial: Partial<User>) => void;
  logout: () => void;
  avatarVersion: number;
  bumpAvatarVersion: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Sayfa yenilemede state sıfırlanmasın diye localStorage'dan başlat.
  // Ama artık sadece okuma için — auth kontrolü buradan yapılır.
  const [user, setUser] = useState<User | null>(() => {
    try {
      const stored = localStorage.getItem('user');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  // Avatar cache-bust — session-scoped. Upload sonrası bump edilir, DB/localStorage'a
  // sızmaz. 0 = bumped değil (query param eklenmez).
  const [avatarVersion, setAvatarVersion] = useState<number>(0);

  const login = useCallback((userData: User) => {
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  }, []);

  const updateUser = useCallback((partial: Partial<User>) => {
    setUser((current) => {
      if (!current) return current;
      const next = { ...current, ...partial };
      localStorage.setItem('user', JSON.stringify(next));
      return next;
    });
  }, []);

  const bumpAvatarVersion = useCallback(() => {
    setAvatarVersion(Date.now());
  }, []);

  const logout = useCallback(() => {
    // Kimlik + liste filtreleri birlikte silinir (bkz. utils/session.ts).
    clearUserSession();
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, isAuthenticated: !!user, login, updateUser, logout, avatarVersion, bumpAvatarVersion }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuthContext must be used within AuthProvider');
  return ctx;
};
